import React from "react";
import { useState } from "react";
import { FiMail, FiMapPin, FiSend, FiUser, FiMessageCircle } from "react-icons/fi";

function Contact() {
    const [formData, setFormData] = useState({ name: "", email: "", message: "" });
    const [sent, setSent] = useState(false);
    const [error, setError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setIsSubmitting(true);

        try {
            // In a real app, you'd send this to your backend API
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            });

            if (response.ok) {
                setSent(true);
                setFormData({ name: "", email: "", message: "" });
            } else {
                setError("Failed to send message. Please try again or email us directly.");
            }
        } catch (err) {
            console.error('Contact form error:', err);
            setError("Failed to send message. Please try again or email us directly.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="pt-24 pb-16 text-white min-h-screen">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-12 text-center">
                    <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
                        Contact Us
                    </h1>
                    <p className="text-blue-200 text-lg max-w-2xl mx-auto">
                        To get in touch with the team, please fill out the form below. We'll get back to you as soon as possible.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                    {/* Contact Form */}
                    <div className="bg-gradient-to-br from-[#021428] to-[#011226] rounded-2xl p-6 md:p-8 shadow-lg border border-white/5">
                        <h2 className="text-2xl font-bold mb-6 text-blue-300 flex items-center gap-2">
                            <FiMessageCircle className="text-blue-400" />
                            Send us a Message
                        </h2>

                        {!sent ? (
                            <>
                                {error && (
                                    <div className="mb-4 p-4 bg-red-700/20 border border-red-500/30 rounded-lg text-red-300 text-sm">
                                        {error}
                                    </div>
                                )}
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-blue-200 mb-2">
                                            Your Name
                                        </label>
                                        <div className="relative">
                                            <FiUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-400" />
                                            <input
                                                type="text"
                                                value={formData.name}
                                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                                className="w-full pl-10 pr-4 py-3 rounded-lg bg-white/5 border border-white/10 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition"
                                                placeholder="John Doe"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-blue-200 mb-2">
                                            Email Address
                                        </label>
                                        <div className="relative">
                                            <FiMail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-400" />
                                            <input
                                                type="email"
                                                value={formData.email}
                                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                                className="w-full pl-10 pr-4 py-3 rounded-lg bg-white/5 border border-white/10 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition"
                                                placeholder="john@example.com"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-blue-200 mb-2">
                                            Message
                                        </label>
                                        <textarea
                                            value={formData.message}
                                            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                            className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition resize-none"
                                            placeholder="Tell us how we can help..."
                                            rows={6}
                                            required
                                        />
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 disabled:cursor-not-allowed rounded-lg transition font-semibold flex items-center justify-center gap-2"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <span className="animate-spin">⏳</span>
                                                Sending...
                                            </>
                                        ) : (
                                            <>
                                                <FiSend />
                                                Send Message
                                            </>
                                        )}
                                    </button>
                                </form>
                            </>
                        ) : (
                            <div className="text-center py-8">
                                <div className="inline-block p-4 bg-green-700/20 border border-green-500/30 rounded-full mb-4">
                                    <svg className="w-12 h-12 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <h3 className="text-2xl font-bold mb-2 text-green-400">Message Sent!</h3>
                                <p className="text-blue-200 mb-6">
                                    Thanks for reaching out. We'll reply shortly.
                                </p>
                                <button
                                    onClick={() => {
                                        setSent(false);
                                        setError("");
                                    }}
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition text-sm"
                                >
                                    Send Another Message
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Contact Information */}
                    <div className="space-y-6">
                        <div className="bg-gradient-to-br from-[#021428] to-[#011226] rounded-2xl p-6 md:p-8 shadow-lg border border-white/5">
                            <h2 className="text-2xl font-bold mb-6 text-blue-300">Get in Touch</h2>
                            <div className="space-y-6">
                                {/* Email */}
                                <div className="flex items-start gap-4">
                                    <div className="p-3 bg-blue-600/20 rounded-lg">
                                        <FiMail className="text-blue-400 text-xl" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-white mb-1">Email</h3>
                                        <a
                                            href="mailto:contact@skylapsegames.com"
                                            className="text-blue-300 hover:text-blue-200 transition underline"
                                        >
                                            contact@skylapsegames.com
                                        </a>
                                        <p className="text-sm text-blue-200 mt-1">
                                            We typically respond within 24-48 hours
                                        </p>
                                    </div>
                                </div>

                                {/* Address */}
                                <div className="flex items-start gap-4">
                                    <div className="p-3 bg-blue-600/20 rounded-lg">
                                        <FiMapPin className="text-blue-400 text-xl" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-white mb-1">Office Location</h3>
                                        <p className="text-blue-200">
                                            Jønningsheiveien 43<br />
                                            4316 Sandnes, Norway
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Additional Info */}
                        <div className="bg-gradient-to-br from-blue-600/10 to-blue-800/10 rounded-xl p-6 border border-blue-500/20">
                            <h3 className="font-semibold text-blue-300 mb-3">Other Ways to Reach Us</h3>
                            <ul className="space-y-2 text-blue-200 text-sm">
                                <li className="flex items-start gap-2">
                                    <span className="text-blue-400 mt-1">•</span>
                                    <span>For press inquiries, please mention "Press" in your subject line</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-blue-400 mt-1">•</span>
                                    <span>For partnership opportunities, include "Partnership" in your message</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-blue-400 mt-1">•</span>
                                    <span>For support related to our games, use the in-game support feature</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Contact;